import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

const ScheduleStatus = () => {
  const { user } = useAuth();
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Generate next 5 days
  const getNext5Days = () => {
    const days = [];
    const today = new Date();
    
    // Get local date components to avoid timezone issues
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    for (let i = 0; i < 5; i++) {
      // Create date using local components
      const date = new Date(year, month, day + i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      days.push({
        date: dateStr,
        displayDate: date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        }),
        isToday: i === 0,
        isTomorrow: i === 1
      });
    }
    return days;
  };

  // Load schedule data for the next 5 days
  const loadScheduleData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError('');
      
      const days = getNext5Days();
      
      // Load beaches data once
      const beachesResponse = await apiClient.get('/api/beaches');
      const beaches = beachesResponse.data;
      
      const schedulePromises = days.map(async (day) => {
        try {
          const response = await apiClient.get(`/api/schedule-assignments/${day.date}`);
          
          // Find user's assignment for this date
          let userAssignment = null;
          let userVehicle = null;
          
          if (response.data.assignments) {
            // Search through all beaches for user's assignment
            for (const [beachId, employees] of Object.entries(response.data.assignments)) {
              const foundEmployee = employees.find(emp => emp.id === user.id);
              if (foundEmployee) {
                // Get beach name from cached beaches data
                const beach = beaches.find(b => b.id === parseInt(beachId));
                
                userAssignment = {
                  beach: beach?.name || 'Unknown Beach',
                  beachDescription: beach?.description,
                  position: employees.indexOf(foundEmployee) + 1,
                  totalAtBeach: employees.length
                };
                
                // Check for vehicle assignment
                if (response.data.vehicleAssignments && response.data.vehicleAssignments[user.id]) {
                  userVehicle = response.data.vehicleAssignments[user.id].vehicle;
                }
                break;
              }
            }
          }
          
          return {
            ...day,
            assignment: userAssignment,
            vehicle: userVehicle,
            hasSchedule: response.data.totalAssignments > 0
          };
        } catch (error) {
          if (error.response?.status === 404) {
            return {
              ...day,
              assignment: null,
              vehicle: null,
              hasSchedule: false
            };
          }
          throw error;
        }
      });

      const results = await Promise.all(schedulePromises);
      setScheduleData(results);
      
    } catch (error) {
      console.error('Failed to load schedule data:', error);
      setError('Failed to load schedule information');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadScheduleData();
  }, [loadScheduleData]);

  if (loading) {
    return (
      <div className="schedule-status-card">
        <div className="card-header">
          <h2 className="card-title">Your Schedule - Next 5 Days</h2>
        </div>
        <div className="schedule-status-loading">
          <div className="loading-spinner"></div>
          <p>Loading your schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedule-status-card">
        <div className="card-header">
          <h2 className="card-title">Your Schedule - Next 5 Days</h2>
        </div>
        <div className="alert alert-error">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-status-card">
      <div className="card-header">
        <h2 className="card-title">Your Schedule - Next 5 Days</h2>
      </div>
      
      <div className="schedule-status-grid">
        {scheduleData.map((day, index) => (
          <div 
            key={day.date} 
            className={`schedule-status-day ${day.isToday ? 'today' : ''} ${day.isTomorrow ? 'tomorrow' : ''} ${index >= 2 ? 'future-day' : ''}`}
          >
            <div className="day-header">
              <div className="day-date">{day.displayDate}</div>
              <div className="day-badge-container">
                {day.isToday && <span className="day-badge today-badge">Today</span>}
                {day.isTomorrow && <span className="day-badge tomorrow-badge">Tomorrow</span>}
              </div>
            </div>
            
            <div className="day-content">
              {!day.hasSchedule ? (
                <div className="no-schedule">
                  <div className="status-icon">📋</div>
                  <div className="status-text">Line up not released</div>
                </div>
              ) : day.assignment ? (
                <div className="has-assignment">
                  <div className="assignment-info">
                    <div className="beach-name">{day.assignment.beach}</div>
                    {day.assignment.beachDescription && (
                      <div className="beach-description">{day.assignment.beachDescription}</div>
                    )}
                    <div className="position-info">
                      Position #{day.assignment.position}
                      {day.assignment.totalAtBeach > 1 && (
                        <span className="total-info"> of {day.assignment.totalAtBeach}</span>
                      )}
                    </div>
                  </div>
                  {day.vehicle && (
                    <div className="vehicle-info">
                      <div className="vehicle-icon">🚗</div>
                      <div className="vehicle-details">
                        <div className="vehicle-name">{day.vehicle.name}</div>
                        {day.vehicle.description && (
                          <div className="vehicle-desc">{day.vehicle.description}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="not-assigned">
                  <div className="status-icon">🏖️</div>
                  <div className="status-text">Not assigned</div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduleStatus; 